import { stringify } from 'query-string'
import {
  CreateResult,
  DataProvider,
  DeleteManyResult,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceResult,
  GetManyResult,
  GetOneResult,
  RaRecord,
  UpdateManyResult,
  UpdateResult,
} from 'react-admin'
import axios from '../../../config/connections'
import { setFormBodyReq } from '../../../utils/form'
import { kebabCase } from 'lodash'
import { InternalRestResourceOptions, RestResourcesOptions } from '../type'

const getResourceOptions = (
  resource: string,
  options: RestResourcesOptions,
): InternalRestResourceOptions => {
  const resourceOptions = options[resource]

  if (!resourceOptions) {
    return {
      fullTextSearchFields: [],
      path: null,
    }
  }

  return {
    fullTextSearchFields: resourceOptions.fullTextSearchFields || [],
    path: resourceOptions.path || null,
  }
}

const normalizeFilter = ({ filter }: GetListParams, resourceOptions): object => {
  const { fullTextSearchFields } = resourceOptions
  const { q, ...restFilters } = filter
  const newFilter = restFilters

  if (fullTextSearchFields.length === 0) return newFilter

  if (q)
    fullTextSearchFields.forEach((searchField) => {
      newFilter[searchField] = q
    })

  return newFilter
}
const parseResourceToUri = (resource: string, options: RestResourcesOptions): string => {
  return options[resource]?.path || kebabCase(resource)
}

const transformResourceByResource = (resource: string, data): RaRecord[] => {
  switch (resource) {
    case 'branches':
      return data.map((d) => ({
        ...d,
        id: d.branch_code,
      }))
    default:
      return data
  }
}
// const transformBranchConf = (resource: string, data): RaRecord => {
//   switch (resource) {
//     case 'branch_config':
//       if (!data?.id) {
//         return {
//           ...data,
//           id: data.branch_id,
//         }
//       }
//       break
//     default:
//       return data
//   }
// }

export const beseDataProvider = (resources: RestResourcesOptions): DataProvider => ({
  getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
    const resourceOptions = getResourceOptions(resource, resources)
    const pagination = params.pagination
    const { field: sortColumn, order: sortDirection } = params.sort
    const query = {
      ...(params?.meta?.isPaginate === false
        ? {}
        : { page: pagination.page, per_page: pagination.perPage }),
      sort_direction: sortDirection,
      sort_column: sortColumn,
      ...normalizeFilter(params, resourceOptions),
    }
    const getListPath: string = params?.meta?.getListPath || ''
    const path = `${getListPath || parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(path)
    return {
      data: transformResourceByResource(resource, response.data.result) || [],
      total: response.data.total,
    }
  },
  getOne: async (resource, { id }): Promise<GetOneResult> => {
    const path = `${parseResourceToUri(resource, resources)}/${id}`

    const response = await axios.get(path)
    return {
      data: response.data.result
    }
  },
  getMany: async (resource, { ids }): Promise<GetManyResult> => {
    const query = { id: ids }
    const path = `${parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(path)
    return {
      data: transformResourceByResource(resource, response.data.result) || [],
    }
  },
  getManyReference: async (resource, params): Promise<GetManyReferenceResult> => {
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    }
    const path = `${parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(path)
    return {
      data: response.data.result,
    }
  },
  create: async (resource, { data }): Promise<CreateResult> => {
    const { metaPath, ...payload } = data
    const path = metaPath || parseResourceToUri(resource, resources)
    const { encType, body } = setFormBodyReq(payload)
    const { data: resData } = await axios.post(path, body, {
      headers: {
        'Content-Type': encType,
      },
    })

    return {
      data: { ...resData.result, id: resData.result.inserted_id },
    }
  },
  update: async (resource, { id, data }): Promise<UpdateResult> => {
    const { metaPath, ...payload } = data
    const basePath = metaPath || `${parseResourceToUri(resource, resources)}/${id}`
    const { encType, body } = setFormBodyReq(payload)

    await axios.put(basePath, body, {
      headers: {
        'Content-Type': encType,
      },
    })
    return {
      data: { id, ...body },
    }
  },
  updateMany: async (resource, { ids, data }): Promise<UpdateManyResult> => {
    const { metaPath, ...payload } = data
    const basePath = metaPath || parseResourceToUri(resource, resources)
    const { encType, body } = setFormBodyReq(payload)
    await Promise.all(
      ids.map((id) =>
        axios.put(`${basePath}/${id}`, body, {
          headers: {
            'Content-Type': encType,
          },
        }),
      ),
    )
    return {
      data: ids,
    }
  },
  delete: async (resource, { id }): Promise<DeleteResult> => {
    const path = `${parseResourceToUri(resource, resources)}/${id}`
    return await axios.delete(path)
  },
  deleteMany: async (resource, { ids }): Promise<DeleteManyResult> => {
    await Promise.all(
      ids.map((id) => axios.delete(`${parseResourceToUri(resource, resources)}/${id}`)),
    )
    return {
      data: ids,
    }
  },
})
